// src/views/hrms/gps/GpsTrackingPage.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  getLiveLocations,
  getEmployeeById,
  getOfficeByTenant,
} from "../../../services/hrms.api";
import GpsStatusCard from "components/hrms/GpsStatusCard";
import { RefreshCcw, Search } from "lucide-react";
import RouteTrackingMap from "../RouteTrackingMap";
import Loading from "components/common/Loading";

export default function GpsTrackingPage() {
  // ---- config ----
  const POLL_MS = 10000;

  const NEAR = 0.2; // km (in office)
  const CLOSE = 0.5; // km (nearby)
  const MODERATE = 1.5; // km (moderate) ; >1.5 => far

  // ---- state ----
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastSync, setLastSync] = useState(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [rateLimitedUntil, setRateLimitedUntil] = useState(null);

  // dynamic office center (from backend)
  const [officeCenter, setOfficeCenter] = useState(null);

  const inflightRef = useRef(false);
  const backoffRef = useRef(POLL_MS);
  const selectedRef = useRef(null);
  const employeeCacheRef = useRef({}); // { [employeeId]: employeeDoc }
  const coordCacheRef = useRef({}); // { "lat,lng": "Some address" }

  // keep selected in a ref so load() doesn't depend on it
  useEffect(() => {
    selectedRef.current = selected;
  }, [selected]);

  const User = localStorage.getItem("user");
  const tenantUser = JSON.parse(User || "{}");
  const tenantId = tenantUser?.tenant;

  // ---- fetch office center for this tenant ----
  useEffect(() => {
    (async () => {
      try {
        const { data } = await getOfficeByTenant(tenantId);
        const items = data?.items || [];
        const primary = items[0];

        if (
          primary &&
          Number.isFinite(primary.lat) &&
          Number.isFinite(primary.lng)
        ) {
          setOfficeCenter({ lat: primary.lat, lng: primary.lng });
        }
      } catch (err) {
        console.error("Failed to load office center", err);
      }
    })();
  }, [tenantId]);

  // ---- helpers ----
  const haversineKm = useCallback((a, b) => {
    if (
      !a ||
      !b ||
      !Number.isFinite(a.lat) ||
      !Number.isFinite(a.lng) ||
      !Number.isFinite(b.lat) ||
      !Number.isFinite(b.lng)
    ) {
      return Infinity;
    }

    const R = 6371;
    const dLat = ((b.lat - a.lat) * Math.PI) / 180;
    const dLng = ((b.lng - a.lng) * Math.PI) / 180;
    const lat1 = (a.lat * Math.PI) / 180;
    const lat2 = (b.lat * Math.PI) / 180;
    const sinDLat = Math.sin(dLat / 2);
    const sinDLng = Math.sin(dLng / 2);
    const c =
      2 *
      Math.asin(
        Math.sqrt(
          sinDLat * sinDLat +
            Math.cos(lat1) * Math.cos(lat2) * (sinDLng * sinDLng)
        )
      );
    return R * c;
  }, []);

  const rangeOf = useCallback(
    (p) => {
      const lat = Number.isFinite(p?.lat)
        ? p.lat
        : Array.isArray(p?.location?.coordinates)
        ? p.location.coordinates[1]
        : undefined;
      const lng = Number.isFinite(p?.lng)
        ? p.lng
        : Array.isArray(p?.location?.coordinates)
        ? p.location.coordinates[0]
        : undefined;

      const origin = officeCenter;
      const km = haversineKm({ lat, lng }, origin);
      if (km <= NEAR) return { label: "In Office", tone: "indigo", km };
      if (km <= CLOSE) return { label: "Nearby", tone: "blue", km };
      if (km <= MODERATE) return { label: "Moderate", tone: "amber", km };
      return { label: "Far", tone: "rose", km };
    },
    [haversineKm, officeCenter]
  );

  const isToday = useCallback((d) => {
    if (!d) return false;

    const dt = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(dt.getTime())) return false;

    const now = new Date();
    return (
      dt.getFullYear() === now.getFullYear() &&
      dt.getMonth() === now.getMonth() &&
      dt.getDate() === now.getDate()
    );
  }, []);

  // ---- load (single-flight, honors Retry-After, backoff + jitter) ----
  const load = useCallback(async () => {
    if (rateLimitedUntil && Date.now() < rateLimitedUntil.getTime()) return;
    if (inflightRef.current) return;

    inflightRef.current = true;
    setLoading(true);

    try {
      const { data } = await getLiveLocations();
      const raw = Array.isArray(data) ? data : data?.items || [];

      const mapped = raw.map((p) => {
        const when = p.ts ?? p.at ?? p.capturedAt ?? new Date().toISOString();
        const ts =
          typeof when === "number" && String(when).length <= 10
            ? new Date(when * 1000)
            : new Date(when);

        // Normalize coords
        let lat = p.lat;
        let lng = p.lng;
        if (
          (!Number.isFinite(lat) || !Number.isFinite(lng)) &&
          p.location?.coordinates
        ) {
          const [gLng, gLat] = p.location.coordinates; // [lng, lat]
          lat = gLat;
          lng = gLng;
        }

        const employeeId = p.employeeId ?? p.employee ?? p.empId ?? "";
        let employeeName = p.employeeName ?? p.name ?? "";

        const locationName =
          p.locationName ||
          p.address ||
          p.placeName ||
          p.formattedAddress ||
          "";

        // reuse cached employee name if present
        const cached = employeeCacheRef.current[employeeId];
        if (!employeeName && cached) {
          const nameFromEmp =
            cached.fullName ||
            cached.name ||
            (cached.firstName && cached.lastName
              ? `${cached.firstName} ${cached.lastName}`
              : undefined);
          if (nameFromEmp) {
            employeeName = nameFromEmp;
          }
        }

        const d = rangeOf({ ...p, lat, lng });
        const active =
          typeof p.active === "boolean"
            ? p.active
            : (Date.now() - ts.getTime()) / 1000 <= 5 * 60;

        return {
          ...p,
          employeeId,
          employeeName,
          locationName,
          lat,
          lng,
          ts,
          ...d,
          active,
        };
      });

      // keep only latest per employee
      const latestByEmp = new Map();
      for (const p of mapped) {
        const k = p.employeeId || p.employeeName || "_";
        const prev = latestByEmp.get(k);
        if (!prev || (p.ts?.getTime?.() || 0) > (prev.ts?.getTime?.() || 0)) {
          latestByEmp.set(k, p);
        }
      }
      const items = Array.from(latestByEmp.values()).sort(
        (a, b) => (b.ts?.getTime?.() || 0) - (a.ts?.getTime?.() || 0)
      );

      // 🔹 HARD FILTER: only today's last ping per employee
      const todayItems = items.filter((p) => isToday(p.ts));

      setPoints(todayItems);
      setLastSync(new Date());

      const sel = selectedRef.current;
      if (sel) {
        const found = todayItems.find((x) => x.employeeId === sel.employeeId);
        setSelected(found || null);
      }

      backoffRef.current = POLL_MS;
    } catch (e) {
      console.error("GPS LOAD ERROR", e);
      const status = e?.response?.status;

      if (status === 429) {
        let retryMs = 0;
        const ra = e?.response?.headers?.["retry-after"];
        if (ra) {
          const n = Number(ra);
          if (Number.isFinite(n)) {
            retryMs = n * 1000;
          } else {
            const t = new Date(ra);
            if (!isNaN(t)) retryMs = Math.max(0, t.getTime() - Date.now());
          }
        }
        if (!retryMs) retryMs = Math.max(backoffRef.current || POLL_MS, 15000);

        const until = new Date(Date.now() + retryMs);
        setRateLimitedUntil(until);

        const next = Math.min((backoffRef.current || POLL_MS) * 2, 60000);
        const jitter = Math.round(next * (0.15 + Math.random() * 0.25));
        backoffRef.current = next + jitter;
      } else {
        const next = Math.min((backoffRef.current || POLL_MS) * 2, 60000);
        const jitter = Math.round(next * (0.1 + Math.random() * 0.2));
        backoffRef.current = next + jitter;
      }
    } finally {
      inflightRef.current = false;
      setLoading(false);
    }
  }, [rangeOf, rateLimitedUntil, isToday]);

  // initial load
  useEffect(() => {
    load();
  }, [load]);

  // Visibility-aware refresh
  useEffect(() => {
    const onVis = () => {
      if (!document.hidden) {
        load();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [load]);

  // quiet backoff polling with setTimeout loop
  useEffect(() => {
    let id;
    let cancelled = false;

    const tick = async () => {
      if (rateLimitedUntil && Date.now() < rateLimitedUntil.getTime()) {
        id = setTimeout(
          tick,
          Math.max(1000, rateLimitedUntil.getTime() - Date.now())
        );
        return;
      }
      if (!document.hidden) {
        await load();
      }
      if (cancelled) return;
      id = setTimeout(tick, backoffRef.current || POLL_MS);
    };

    tick();
    return () => {
      cancelled = true;
      if (id) clearTimeout(id);
    };
  }, [load, rateLimitedUntil]);

  // ---- hydrate employee details using getEmployeeById ----
  useEffect(() => {
    async function hydrateEmployees() {
      if (!points.length) return;

      const cache = employeeCacheRef.current;

      const idsToFetch = Array.from(
        new Set(
          points
            .filter(
              (p) => p.employeeId && !p.employeeName && !cache[p.employeeId]
            )
            .map((p) => p.employeeId)
        )
      );

      if (!idsToFetch.length) return;

      try {
        await Promise.all(
          idsToFetch.map(async (id) => {
            try {
              const { data } = await getEmployeeById(id);
              cache[id] = data;
            } catch (err) {
              console.error("Failed to fetch employee", id, err);
            }
          })
        );

        setPoints((prev) =>
          prev.map((p) => {
            if (p.employeeName) return p;

            const emp = cache[p.employeeId];
            if (!emp) return p;

            const nameFromEmp =
              emp.fullName ||
              emp.name ||
              (emp.firstName && emp.lastName
                ? `${emp.firstName} ${emp.lastName}`
                : undefined);

            return nameFromEmp ? { ...p, employeeName: nameFromEmp } : p;
          })
        );
      } catch (err) {
        console.error("Error hydrating employees", err);
      }
    }

    hydrateEmployees();
  }, [points]);

  // ---- hydrate human-readable location from coordinates ----
  useEffect(() => {
    let cancelled = false;

    async function hydrateLocations() {
      if (!points.length) return;

      const cache = coordCacheRef.current;

      // keys to look up only when:
      // - point has no locationName yet
      // - coords are valid
      // - we do NOT already have a cached SUCCESS for this coord
      const keysToLookup = Array.from(
        new Set(
          points
            .filter(
              (p) =>
                !p.locationName &&
                Number.isFinite(p.lat) &&
                Number.isFinite(p.lng)
            )
            .map((p) => `${p.lat.toFixed(4)},${p.lng.toFixed(4)}`)
        )
      ).filter((k) => cache[k] === undefined); // undefined = never successfully resolved yet

      if (!keysToLookup.length) return;

      await Promise.all(
        keysToLookup.map(async (key) => {
          const [latStr, lngStr] = key.split(",");
          const lat = parseFloat(latStr);
          const lng = parseFloat(lngStr);

          try {
            const res = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}`
            );
            const data = await res.json();

            const label =
              data?.name ||
              data?.display_name ||
              [data?.address?.suburb, data?.address?.city, data?.address?.state]
                .filter(Boolean)
                .join(", ");

            // ❗ only cache if we actually got a label
            if (!label) return;

            cache[key] = label;

            if (cancelled) return;

            // update all points that match this coord
            setPoints((prev) =>
              prev.map((p) => {
                if (p.locationName) return p;

                const k =
                  Number.isFinite(p.lat) && Number.isFinite(p.lng)
                    ? `${p.lat.toFixed(4)},${p.lng.toFixed(4)}`
                    : "";

                if (k === key) {
                  return { ...p, locationName: label };
                }
                return p;
              })
            );
          } catch (err) {
            // on error: DO NOT cache anything
            // so we can retry on next poll or when points change
            console.error("Reverse geocode failed", err);
          }
        })
      );
    }

    hydrateLocations();

    return () => {
      cancelled = true;
    };
  }, [points]);

  // ---- derived ----
  const activeOnField = points.filter((p) => p.active).length;

  const rangeCounts = useMemo(() => {
    return points.reduce(
      (acc, p) => {
        if (p.label === "In Office") acc.inOffice++;
        else if (p.label === "Nearby") acc.nearby++;
        else if (p.label === "Moderate") acc.moderate++;
        else acc.far++;
        return acc;
      },
      { inOffice: 0, nearby: 0, moderate: 0, far: 0 }
    );
  }, [points]);

  const filtered = useMemo(() => {
    const k = q.trim().toLowerCase();
    if (!k) return points;
    return points.filter((p) => {
      const kmInt = String(Math.round(p.km || 0));
      return (
        (p.employeeName || "").toLowerCase().includes(k) ||
        (p.employeeId || "").toLowerCase().includes(k) ||
        (p.label || "").toLowerCase().includes(k) ||
        (p.locationName || "").toLowerCase().includes(k) ||
        kmInt.includes(k)
      );
    });
  }, [points, q]);

  const since = (d) => {
    if (!d) return "—";
    const sec = Math.max(1, Math.round((Date.now() - d.getTime()) / 1000));
    if (sec < 60) return `${sec}s ago`;
    const min = Math.round(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.round(min / 60);
    return `${hr}h ago`;
  };

  const n = (v, d = 0) => (Number.isFinite(v) ? v : d);

  const origin = officeCenter;

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Title + controls */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <h2 className="text-xl font-semibold dark:text-white">
          Real-time GPS Tracking
        </h2>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search employee / range / location…"
              className="dark:bg-transparent w-64 rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:border-white/10 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700"
            />
          </div>
          <button
            onClick={load}
            className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm hover:bg-gray-50 dark:border-white/10 dark:text-white dark:hover:bg-white/10"
            title="Refresh now"
          >
            <RefreshCcw className="h-4 w-4" /> Refresh
          </button>

          {selected && (
            <button
              onClick={() => setSelected(null)}
              className="rounded-lg border px-3 py-2 text-sm hover:bg-gray-50 dark:border-white/10 dark:text-white dark:hover:bg-white/10"
              title="Clear map selection"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Rate limit banner */}
      {rateLimitedUntil && Date.now() < rateLimitedUntil.getTime() && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Server is rate-limiting. Auto-refresh will resume at{" "}
          {rateLimitedUntil.toLocaleTimeString()}.
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-1 gap-4 dark:text-white md:grid-cols-3">
        <GpsStatusCard title="Active On-Field" value={activeOnField} />
        <GpsStatusCard title="Total Devices" value={points.length} />
        <GpsStatusCard
          title="Last Sync"
          value={lastSync ? since(lastSync) : "—"}
        />
      </div>

      {/* Range cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <RangeCard
          label="In Office"
          tone="amber"
          value={rangeCounts.inOffice}
        />
        <RangeCard label="Nearby" tone="blue" value={rangeCounts.nearby} />
        <RangeCard label="Moderate" tone="amber" value={rangeCounts.moderate} />
        <RangeCard label="Far" tone="blue" value={rangeCounts.far} />
      </div>

      {/* Map area */}
      <div className="relative h-[420px] w-full rounded-2xl border border-gray-200 bg-gray-100 dark:border-white/10 dark:bg-white/5">
        {selected ? (
          <>
            <RouteTrackingMap
              origin={[origin.lat, origin.lng]}
              courier={[selected.lat, selected.lng]}
              className="h-full w-full rounded-2xl"
            />
            <div className="pointer-events-none absolute bottom-3 left-3 z-10 rounded-lg bg-white/90 px-3 py-2 text-xs shadow dark:bg-navy-900/80">
              <div className="font-semibold text-gray-800 dark:text-white">
                {selected.employeeName || selected.employeeId}
              </div>
              <div className="text-gray-600 dark:text-gray-300">
                {n(selected.lat).toFixed(5)}, {n(selected.lng).toFixed(5)} •{" "}
                {n(selected.km).toFixed(2)} km from office
              </div>
              {selected.locationName && (
                <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-300">
                  {selected.locationName}
                </div>
              )}
              <div className="mt-1">
                <RangePill label={selected.label} tone={selected.tone} />
              </div>
            </div>
          </>
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-gray-600 dark:text-white/70">
            Map placeholder — {points.length} live{" "}
            {points.length === 1 ? "point" : "points"} loaded
            {lastSync ? ` • refreshed ${since(lastSync)}` : ""}
          </div>
        )}
      </div>

      {/* Live table */}
      <div className="rounded-2xl border border-gray-200 dark:border-white/10">
        <div className="da border-b border-gray-200 px-4 py-3 text-sm font-semibold dark:border-white/10 dark:text-white">
          Live Locations
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-50 text-gray-600 dark:bg-white/5 dark:text-white/70">
              <tr>
                <th className="px-4 py-2">Employee</th>
                <th className="px-4 py-2">Coords</th>
                <th className="px-4 py-2">Distance</th>
                <th className="px-4 py-2">Range</th>
                {/* NEW */}
                <th className="px-4 py-2">Location</th>
                <th className="px-4 py-2">Updated</th>
                <th className="px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan="7"
                    className="px-4 py-6 text-center text-gray-500"
                  >
                    <Loading />
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td
                    colSpan="7"
                    className="px-4 py-6 text-center text-gray-500"
                  >
                    No live points.
                  </td>
                </tr>
              ) : (
                filtered.map((p) => (
                  <tr
                    key={`${p.employeeId}-${p.ts?.getTime?.() ?? ""}`}
                    className="cursor-pointer border-t border-gray-100 transition-colors hover:bg-gray-50 dark:border-white/10 dark:hover:bg-white/5"
                    onClick={() => setSelected(p)}
                    title="Click to preview on map"
                  >
                    <td className="px-4 py-2">
                      <div className="font-medium text-navy-700 dark:text-white">
                        {p.employeeName || p.employeeId}
                      </div>
                      <div className="text-xs text-gray-500 ">
                        {p.employeeId}
                      </div>
                    </td>
                    <td className="px-4 py-2 dark:text-white">
                      {n(p.lat).toFixed(5)}, {n(p.lng).toFixed(5)}
                    </td>
                    <td className="px-4 py-2 dark:text-white">
                      {n(p.km).toFixed(2)} km
                    </td>
                    <td className="px-4 py-2 dark:text-white">
                      <RangePill label={p.label} tone={p.tone} />
                    </td>
                    {/* NEW: coord-based location name */}
                    <td className="px-4 py-2 dark:text-white">
                      {p.locationName || "—"}
                    </td>
                    <td className="px-4 py-2 dark:text-white">{since(p.ts)}</td>
                    <td className="px-4 py-2 dark:text-white">
                      <span
                        className={`rounded-full px-2 py-1 text-xs font-medium ${
                          p.active
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {p.active ? "Active" : "Idle"}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ---------- small UI helpers ---------- */
function RangeCard({ label, value, tone = "emerald" }) {
  const toneMap = {
    emerald: "bg-emerald-100 text-emerald-700",
    blue: "bg-blue-100 text-blue-700",
    amber: "bg-amber-100 text-amber-700",
    rose: "bg-rose-100 text-rose-700",
  };
  return (
    <div className="flex items-center justify-between rounded-2xl border border-gray-200 p-4 dark:border-white/10">
      <div className="text-sm text-gray-600 dark:text-white/80">{label}</div>
      <span
        className={`rounded-full px-2 py-1 text-xs font-semibold ${toneMap[tone]}`}
      >
        {value}
      </span>
    </div>
  );
}

function RangePill({ label, tone = "emerald" }) {
  const toneMap = {
    emerald: "bg-emerald-100 text-emerald-700",
    blue: "bg-blue-100 text-blue-700",
    amber: "bg-amber-100 text-amber-700",
    rose: "bg-rose-100 text-rose-700",
  };
  return (
    <span
      className={`rounded-full px-2 py-1 text-xs font-semibold ${toneMap[tone]}`}
    >
      {label}
    </span>
  );
}
